<template>
  <div
    ref="triggerRef"
    class="custom-editor__align-dropdown"
  >
    <button
      :tabindex="-1"
      class="custom-editor__button custom-editor__align-dropdown__trigger heading-level heading-level-3"
      type="button"
      @click.prevent.stop="toggleDropdown"
    >
      <icon
        :data="activeIcon"
        width="1em"
        height="1em"
      />
      <icon
        :data="chevronIcon"
        class="custom-editor__align-dropdown__chevron"
        width="0.6em"
        height="0.6em"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="custom-editor__align-dropdown__menu"
        :style="menuStyle"
      >
        <button
          v-for="option in alignOptions"
          :key="option.align"
          :tabindex="-1"
          :title="option.title"
          class="custom-editor__button heading-level heading-level-3"
          :class="{ 'is-active': editor.isActive({ textAlign: option.align }) }"
          type="button"
          @click.prevent.stop="selectAlign(option.align)"
        >
          <icon
            :data="option.icon"
            width="1em"
            height="1em"
          />
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import alignCenterIcon from "@icon/align-center.svg"
import alignJustifyIcon from "@icon/align-justify.svg"
import alignLeftIcon from "@icon/align-left.svg"
import alignRightIcon from "@icon/align-right.svg"
import chevronSvg from "@icon/chevron.svg"
import { Editor } from "@tiptap/vue-3"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps<{
  editor: Editor
}>()

const triggerRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const menuStyle = ref<Record<string, string>>({})

const alignOptions = [
  { align: "left", title: "Left", icon: alignLeftIcon },
  { align: "center", title: "Center", icon: alignCenterIcon },
  { align: "right", title: "Right", icon: alignRightIcon },
  { align: "justify", title: "Justify", icon: alignJustifyIcon }
]

const chevronIcon = chevronSvg

const activeIcon = computed(() => {
  const active = alignOptions.find(o => props.editor.isActive({ textAlign: o.align }))
  return active?.icon ?? alignLeftIcon
})

function toggleDropdown() {
  if (isOpen.value) {
    isOpen.value = false
    return
  }
  const rect = triggerRef.value!.getBoundingClientRect()
  menuStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    zIndex: "9999"
  }
  isOpen.value = true
}

function selectAlign(align: string) {
  props.editor.chain().focus().setTextAlign(align).run()
  isOpen.value = false
}

function onDocumentClick() {
  isOpen.value = false
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick)
})
</script>

<style lang="scss">
@import "@/assets/scss/utils/mixins/_flex";

.custom-editor__align-dropdown {
  position: relative;

  @include flex;

  &__trigger {
    @include flex;

    gap: 2px;
  }

  &__chevron {
    opacity: 0.6;
  }

  &__menu {
    @include flex;

    padding: 4px;
    border: 1px solid var(--monochrome-02);
    border-radius: 6px;
    background-color: var(--monochrome-00);
    box-shadow: var(--center-01);
    gap: 2px;
  }
}

.dark .custom-editor__align-dropdown__menu {
  border-color: var(--monochrome-30);
}
</style>
